import React, { useCallback, useEffect, useState } from 'react';
import BackBtn from '../Button/BackBtn';
import Spacing from '../Spacing';
import { PanelBottomOpen, Search } from 'lucide-react';
import AverateRevenuePerEncounterDataTable from '../DataTable/AverateRevenuePerEncounterDataTable';
import DataTablePagination from '../DataTablePagination';
import TabMenu from '../TabMenu';
import { can_visit, debounce, generateRevenueTableData, getDateString, getPractice, getSpeciality, notify, numberWithCommas, userMessageTemplates } from '../../helpers';
import { fetchAverageRevenue } from '../../services/averageRevenue';
import Filter from '../Filter';
import { useDocumentTitle } from '../../hooks/customDocumentTitleHook';
import FilterSummary from '../UI/FilterSummary';
import { fetchFilterData } from '../../services/saveFilter';
import SaveFilter from '../Filter/SaveFilter';
import FilterSaveConfirmationModal from '../Modal/FilterSaveConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { addToWaitingRoom } from '../../services/patients';

export default function AverateRevenuePerEncounter({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, waitingRoomProviders}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }
  let dateStr = getDateString();

  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [dataLoading, setDataLoading] = useState(true);
  const [revenue, setRevenue] = useState([]);
  const [messageTemplate] = useState(userMessageTemplates());
  const [selectAll, setSelectAll] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    page: 1,
    per_page: perPage || 10,
    year: currentYear,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    mobile_phone: "",
    city: "",
    state: "",
    zip: "",
    insurance_ids: [],
    start_age: "",
    end_age: "",
    last_visit: "",
    sex: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);
  const [patientFormData, setPatientFormData] = useState(filter);
  const [isFilterCleared, setIsFilterCleared] = useState(false);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const [paginationCount, setPaginationCount] = useState({
    totalCount: 0,
    totalPages: 0,
    account_id: account_id,
    location_id: location_id,
  });

  useEffect(() => {
    loadAverageRevenueList(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  
  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);
  
  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const loadAverageRevenueList = async (filter) => {
    setFilter(filter);
    let isCanceled = false;

    try {
      const revenueResponse = await fetchAverageRevenue(filter);
      const generateData = generateRevenueTableData(revenueResponse.data.results);
      setRevenue(generateData);
      setPaginationCount({
        totalCount: revenueResponse.data.count,
        totalPages: revenueResponse.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadAverageRevenueList ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
    if (checkedIds.length < 1) {
      setSelectAll(false);
    }
  };

  const tabMenuData = [
    `${currentYear}`,
    `${currentYear - 1}`,
  ];

  const getRevenueByYear = (selectedTabValue) => {
    const tab = selectedTabValue.split(" ")[0];
    const tabValue = tab;
    setFilter({ ...filter, year: tabValue, page: 1 });
    setPatientFormData({ ...filter, year: tabValue, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleClearFilters = () => {
    setIsFilterCleared(true);
  };
  const handleResetFilterStatus = () => {
    setIsFilterCleared(false);
  };

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setRevenue((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };
  
  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setRevenue((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",
    }));
  };

  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "averate-revenue-per-encounter",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const handleAddToWaitingRoom = async (data, id) => {
    const postDataObject = {
      account_id: account_id,
      location_id: location_id,
      speciality_id: specilityId,
      patient_id: data.id,
      dos: dateStr,
      care_gap: [],
      review_sent: false,
      provider_id: id
    };

    try {
      await addToWaitingRoom(postDataObject);
      notify("success", "Patient added to waiting room successfully");
    } catch (error) {
      notify("error", error?.response?.data?.detail);
    }
  };

  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  useDocumentTitle("Average Revenue");

  return (
    <>
      <BackBtn href="/" btnText="Back to home" />
      <Spacing lg="0" sm="24" />
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#898E87" }}
          >
            <PanelBottomOpen width={18} height={18} />
          </i>
          Average payment per encounter
          <span>{numberWithCommas(paginationCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="10" sm="10" />
      <TabMenu
        tabMenuData={tabMenuData}
        filter={false}
        handleSelectTab={getRevenueByYear}
        setDataLoading={setDataLoading}
      />
      <Spacing lg="20" sm="20" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form width_100">
            <input
              type="search"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>

          <div className="docpro_data_table_btns small_filter_btns3 p-0">
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadAverageRevenueList}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <Filter
              filterType="patient"
              reloadData={loadAverageRevenueList}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              selectedInsuranceOptions={selectedInsuranceOptions}
              setSelectedInsuranceOptions={setSelectedInsuranceOptions}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              setShowSave={setShowSave}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={filter}
            setFilter={setFilter}
            loadData={loadAverageRevenueList}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setPreviousScrollPosition={setPreviousScrollPosition}
            setDataLoading={setDataLoading}
          /> 
        </div>
        <AverateRevenuePerEncounterDataTable
          tableData={revenue}
          handleSelectAll={() => handleSelectAll(revenue)}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          messageTemplate={messageTemplate}
          reloadData={loadAverageRevenueList}
          reloadDataFilter={filter}
          account_id={account_id}
          location_id={location_id}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          setLetterLimit={setLetterLimit}
          handleAddToWaitingRoom={handleAddToWaitingRoom}
          handleReferralNavigation={handleReferralNavigation}
          user_id={user_id}
          waitingRoomProviders={waitingRoomProviders}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(revenue.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={paginationCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={paginationCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="averate-revenue-per-encounter"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}